import React, { useRef, useState, useCallback, useEffect } from 'react';
import { DEMO_PIPELINE_DRAFT_THUMB, DEMO_PIPELINE_MEDIA_THUMB } from './demoStockImages';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ClipboardList, Film, Clapperboard } from 'lucide-react';
import { DemoStage } from './DemoChrome';
import { gentleCrossfade } from './motionPresets';
import { useDemoAutoplay } from './useDemoAutoplay';
import { PipelineMiniCard } from './PipelineMiniCard';
import './marketingDemos.css';

const APPROVAL_COLUMNS = [
  { id: 'editing', label: 'Editing', tone: 'editing' },
  { id: 'internal', label: 'Internal review', tone: 'internal' },
  { id: 'changes', label: 'Changes requested', tone: 'changes' },
  { id: 'client', label: 'Client review', tone: 'client' },
  { id: 'approved', label: 'Approved', tone: 'approved' },
];

const PIPELINE_ROWS = [
  {
    id: 'planning',
    title: 'Planning',
    Icon: ClipboardList,
    color: '#6366f1',
    item: 'April content brief',
    startColumn: 0,
  },
  {
    id: 'media',
    title: 'Library media',
    Icon: Film,
    color: '#0f766e',
    item: 'Brand interview',
    filename: 'brand-interview.mp4',
    startColumn: 0,
  },
  {
    id: 'drafts',
    title: 'Drafts',
    Icon: Clapperboard,
    color: '#3b82f6',
    item: 'Instagram reel',
    filename: 'reel-cut.mp4',
    startColumn: 0,
  },
];

const ROW_ORDER = ['planning', 'media', 'drafts'];

const initialColumns = () =>
  Object.fromEntries(PIPELINE_ROWS.map((row) => [row.id, row.startColumn]));

export default function PipelineApprovalDemo() {
  const rootRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const [columnByRow, setColumnByRow] = useState(initialColumns);
  const [advancingRowId, setAdvancingRowId] = useState(null);

  const onAutoplayTick = useCallback(() => {
    setColumnByRow((prev) => {
      const nextRowToAdvance = ROW_ORDER.find(
        (rowId) => (prev[rowId] ?? 0) < APPROVAL_COLUMNS.length - 1,
      );

      if (!nextRowToAdvance) {
        setAdvancingRowId(null);
        return initialColumns();
      }

      setAdvancingRowId(nextRowToAdvance);
      return {
        ...prev,
        [nextRowToAdvance]: (prev[nextRowToAdvance] ?? 0) + 1,
      };
    });
  }, []);

  useDemoAutoplay(rootRef, onAutoplayTick, 950);

  useEffect(() => {
    [DEMO_PIPELINE_MEDIA_THUMB, DEMO_PIPELINE_DRAFT_THUMB].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  return (
    <div ref={rootRef}>
      <DemoStage
        className="mkt-demo-stage--pipeline-visible"
        visualClassName="mkt-demo-visual--pipeline"
      >
        <div className="mkt-pipeline-approval-grid" role="table" aria-label="Approval process by deliverable">
          <div className="mkt-pipeline-approval-header-row" role="row">
            <span className="mkt-pipeline-approval-corner" role="columnheader">
              <strong>Deliverable</strong>
            </span>
            {APPROVAL_COLUMNS.map((col) => (
              <span
                key={col.id}
                className={`mkt-pipeline-approval-col-head mkt-pipeline-approval-col-head--${col.tone}`}
                role="columnheader"
              >
                <strong>{col.label}</strong>
              </span>
            ))}
          </div>

          {PIPELINE_ROWS.map((row) => {
            const RowIcon = row.Icon;
            const activeCol = columnByRow[row.id] ?? 0;
            const isAdvancingRow = advancingRowId === row.id;

            return (
              <div
                key={row.id}
                className={`mkt-pipeline-approval-row${isAdvancingRow ? ' mkt-pipeline-approval-row--active' : ''}`}
                role="row"
              >
                <div className="mkt-pipeline-approval-row-shell">
                  <div className="mkt-pipeline-approval-row-label" role="rowheader">
                    <span className="mkt-pipeline-approval-row-icon" style={{ background: row.color }}>
                      <RowIcon className="ui-icon-sm" color="#fff" aria-hidden />
                    </span>
                    <span className="mkt-pipeline-approval-row-title">
                      <strong>{row.title}</strong>
                    </span>
                  </div>

                  {APPROVAL_COLUMNS.map((col, colIndex) => {
                    const isFilled = colIndex === activeCol;
                    return (
                      <div
                        key={col.id}
                        className={`mkt-pipeline-approval-cell mkt-pipeline-approval-cell--${col.tone}`}
                        role="cell"
                      >
                        <span className="mkt-pipeline-approval-cell-empty" aria-hidden />
                        <AnimatePresence mode="sync" initial={false}>
                          {isFilled ? (
                            <motion.div
                              key={`${row.id}-${colIndex}`}
                              className="mkt-pipeline-approval-card-slot mkt-pipeline-approval-card-slot--auto"
                              variants={gentleCrossfade}
                              initial={reduceMotion ? false : 'initial'}
                              animate="animate"
                              exit={reduceMotion ? undefined : 'exit'}
                            >
                              <PipelineMiniCard
                                rowId={row.id}
                                row={row}
                                stageLabel={col.label}
                                stageTone={col.tone}
                              />
                            </motion.div>
                          ) : null}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </DemoStage>
    </div>
  );
}
